import React, {
  forwardRef,
  useCallback,
  useMemo,
  useState
} from "react";
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import message from "components/CustomMessage";
import { FieldTypes } from "utils/formUtils";
import { uniqBy, isEmpty } from "lodash";

import GenericMappingForm from "../GenericMappingForm";

const safeJsonParse = (value, fallback = []) => {
    if (!value) return fallback;
    try {
        return JSON.parse(value);
    } catch {
        return value;
    }
};

const ArnicaMappingForm = forwardRef(
    function ArnicaMappingForm({ disableProjectFields = false, ...props }, ref) {

        const [mappedProjectList] = useState(() => {
            // this state contains the already mapped application/site
            // This is stored because the project and version dropdowns are paginated
            // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
            if (props.record) {
                const { repository, repositoryName } = props.record;
                // Parse JSON stringified arrays from backend
                const repositoryArray = safeJsonParse(repository);
                const repositoryNameArray = safeJsonParse(repositoryName);

                // For discovery configs
                if (!Array.isArray(repositoryArray)) {
                    return {
                        projects: repositoryNameArray ? [{ id: repositoryArray, name: repositoryNameArray }] : [],
                    };
                }

                // Create projects array with id-name mapping
                const projects = repositoryArray.map((id, index) => ({
                    id: id,
                    name: repositoryNameArray[index]
                }));

                return { projects };
            }
            return { projects: [] };
        })

        const [projects, setProjects] = useState(mappedProjectList.projects);

        const setOptionsFromPagination = (option) => {
            setProjects(prev => uniqBy([...prev,...option], 'id'));
        }

        const fetchProjects = useCallback((params) => {
            const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId,...params });
            promise.then((response) => {
                let { projects: fetchedProjects } = response;
                if(mappedProjectList['projects'].length > 0 && !isEmpty(fetchedProjects)){
                    fetchedProjects = [...fetchedProjects, ...mappedProjectList['projects']];
                }
                setOptionsFromPagination(fetchedProjects);
            }).catch((error) => {
                message.error("Problem fetching repositories");
            });
            return { promise, cancel };
        }, [props.toolName, props.loginConfigId, mappedProjectList])

        const getExtraFieldsData = useCallback((config) => {
            const { repository } = config;

            // Handle both array (multi-select) and string (backward compatibility)
            const repositoryIds = Array.isArray(repository) ? repository : [repository];

            // Map IDs to names from the projects state
            const data = projects.reduce((acc, project) => {
                if (repositoryIds.includes(project.id)) {
                    acc.repositoryIds.push(project.id);
                    acc.repositoryNames.push(project.name);
                }
                return acc;
            }, { repositoryIds: [], repositoryNames: [] });

            return {
                repository: JSON.stringify(data.repositoryIds),
                repositoryName: JSON.stringify(data.repositoryNames),
                ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id }),
            };
        }, [projects]);
        
        const extraFields = useMemo(() => ([{
            name: 'repository',
            colSpan: '12',
            type: FieldTypes.SELECT_WITH_PAGINATED_DATA,
            label: 'Repository',
            required: true,
            disabled: disableProjectFields,
            props: {
                mode: "multiple",
                autoFocus: !disableProjectFields,
                api: fetchProjects
            },
        }]), [disableProjectFields, fetchProjects])

        const parseExtraFieldsDataFromResponse = useCallback((config) => {
            return {
                repository: safeJsonParse(config.repository),
                repositoryName: safeJsonParse(config.repositoryName),
                ...(config.discovery_config_id && { discovery_config_id: config.discovery_config_id }),
            };
        }, []);
        
        return (
        <GenericMappingForm
            ref={ref}
            getExtraFieldsData={getExtraFieldsData}
            parseExtraFieldsDataFromResponse={parseExtraFieldsDataFromResponse}
            extraFields={extraFields}
            {...props}
        />
        )
    }
)

export default ArnicaMappingForm;